import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { fetchApi } from '../../api/fetch';
import { Button } from '../../components/ui/button';

type ViewState = 'default' | 'didnt-get-it' | 'skipped';

const SAMPLE_POOL: Array<{ raw: string; domain: 'personal' | 'work' | 'home' }> = [
  { raw: 'call dentist, been putting it off forever', domain: 'personal' },
  { raw: 'renew car insurance, think its due soon??', domain: 'personal' },
  { raw: 'pick up dry cleaning + groceries this weekend', domain: 'personal' },
  { raw: 'finish the budget thing for Lisa before end of month', domain: 'work' },
  { raw: 'follow up with sarah on the proposal she sent last week', domain: 'work' },
  { raw: 'prep for monday standup, pull metrics from last sprint', domain: 'work' },
  { raw: 'need to follow up with that contractor about the quote', domain: 'home' },
  { raw: 'kids school thing — something about wednesday, check the calendar', domain: 'home' },
  { raw: 'fix the kitchen drawer that keeps sticking', domain: 'home' },
];

function pickSamples(): typeof SAMPLE_POOL {
  // One per domain
  const byDomain = (d: 'personal' | 'work' | 'home') => {
    const pool = SAMPLE_POOL.filter((s) => s.domain === d);
    return pool[Math.floor(Math.random() * pool.length)];
  };
  return [byDomain('personal'), byDomain('work'), byDomain('home')];
}

export function WizardStep1Email() {
  const { user } = useAuth();
  const { refetch } = useOnboarding();
  const [view, setView] = useState<ViewState>('default');
  const [dannyAddress, setDannyAddress] = useState<string | null>(null);
  const [showSpamHint, setShowSpamHint] = useState(false);
  const [pulseHint, setPulseHint] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [textareaValue, setTextareaValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [checkingReply, setCheckingReply] = useState(false);
  const [noReplyYet, setNoReplyYet] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const samples = useMemo(() => pickSamples(), []);

  // Load (or auto-init) the user's Danny capture address
  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const data = await fetchApi<{ address: string }>('/users/me/capture-email');
        if (alive) setDannyAddress(data.address);
      } catch {
        try {
          await fetchApi('/users/me/capture-email/init', { method: 'POST' });
          const data = await fetchApi<{ address: string }>('/users/me/capture-email');
          if (alive) setDannyAddress(data.address);
        } catch (err) {
          console.warn('[WizardStep1] failed to init capture email', err);
        }
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, []);

  // Pulse the "I didn't get the email" link after 5 minutes (prototype: 30s)
  useEffect(() => {
    if (view !== 'default') return;
    const pulseT = setTimeout(() => setPulseHint(true), 30_000);
    const spamT = setTimeout(() => setShowSpamHint(true), 60_000);
    return () => {
      clearTimeout(pulseT);
      clearTimeout(spamT);
    };
  }, [view]);

  // Resend cooldown ticker
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const addressDisplay = dannyAddress ?? 'tasks-…@dev.askdanny.to';
  const firstName = user?.name?.split(' ')[0] || 'there';

  const emailBody = [
    `Hey ${firstName},`,
    '',
    "I'm Danny. Reply to this email with anything on your plate —",
    'rough notes, half-formed thoughts, things you keep meaning to do.',
    "I'll turn it into a clean task list.",
    '',
    'Try these as a starting point:',
    ...samples.map((s) => `- ${s.raw}`),
  ].join('\n');

  const mailtoHref = dannyAddress
    ? `mailto:${dannyAddress}?subject=${encodeURIComponent('Re: Welcome — your first tasks')}&body=${encodeURIComponent(emailBody)}`
    : '#';

  // Provider deep links — sniff the user's email domain to highlight
  const userDomain = (user?.email || '').split('@')[1]?.toLowerCase() || '';
  const isGmail = userDomain === 'gmail.com' || userDomain === 'googlemail.com';
  const isOutlook = /outlook|hotmail|live|msn/.test(userDomain);

  // "I replied" — force an immediate onboarding refetch instead of waiting
  // for the 3s poll. If the reply has been processed, emailDone flips and the
  // parent OnboardingWizard advances us off this screen automatically. If not,
  // surface a gentle "haven't seen it yet" so the user isn't left guessing.
  // We inspect refetch's returned data directly rather than racing against the
  // re-render — old 1.2s setTimeout would lie on slow networks.
  async function handleCheckForReply() {
    setCheckingReply(true);
    setNoReplyYet(false);
    try {
      const fresh = await refetch();
      setCheckingReply(false);
      // If emailDone flipped, OnboardingWizard's render will swap us off this
      // screen on the next frame — no further work needed here.
      if (!fresh.emailDone) setNoReplyYet(true);
    } catch {
      setCheckingReply(false);
      setNoReplyYet(true);
    }
  }

  async function handleResend() {
    setResending(true);
    try {
      await fetchApi('/onboarding/resend-welcome', { method: 'POST' });
      setResendCooldown(60);
    } catch (err) {
      // 429 means the user already resent recently — keep the cooldown
      // visible so they understand why nothing happened.
      const message = err instanceof Error ? err.message : String(err);
      if (/rate-limit|too many|429/i.test(message)) {
        setResendCooldown(60);
      } else {
        console.warn('[WizardStep1] resend failed', err);
      }
    } finally {
      setResending(false);
    }
  }

  async function handleSubmit() {
    if (!textareaValue.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await fetchApi('/onboarding/brain-dump', {
        method: 'POST',
        body: JSON.stringify({ text: textareaValue }),
      });
      refetch();
    } catch (err) {
      console.error('[WizardStep1] brain-dump failed', err);
      setSubmitError('Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex h-screen w-full bg-white text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100">
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-zinc-200 dark:bg-zinc-800">
        <div className="h-full w-1/2 bg-primary-500 transition-all" />
      </div>

      <div className="flex-1 flex items-start justify-center overflow-y-auto px-6 py-12 sm:py-20">
        <div className="w-full max-w-md">
          {view === 'default' && (
            <DefaultView
              firstName={firstName}
              addressDisplay={addressDisplay}
              dannyAddress={dannyAddress}
              samples={samples}
              mailtoHref={mailtoHref}
              userEmail={user?.email}
              isGmail={isGmail}
              isOutlook={isOutlook}
              pulseHint={pulseHint}
              showSpamHint={showSpamHint}
              checkingReply={checkingReply}
              noReplyYet={noReplyYet}
              onCheckForReply={handleCheckForReply}
              onDidntGetIt={() => setView('didnt-get-it')}
              onSkip={() => setView('skipped')}
            />
          )}

          {view === 'didnt-get-it' && (
            <DidntGetItView
              addressDisplay={addressDisplay}
              mailtoHref={mailtoHref}
              dannyAddress={dannyAddress}
              isGmail={isGmail}
              isOutlook={isOutlook}
              resending={resending}
              resendCooldown={resendCooldown}
              textareaValue={textareaValue}
              submitting={submitting}
              submitError={submitError}
              onTextareaChange={setTextareaValue}
              onSubmit={handleSubmit}
              onResend={handleResend}
              onBack={() => setView('default')}
              onSkip={() => setView('skipped')}
            />
          )}

          {view === 'skipped' && (
            <SkippedView
              textareaValue={textareaValue}
              submitting={submitting}
              submitError={submitError}
              onTextareaChange={setTextareaValue}
              onSubmit={handleSubmit}
              onContinueWithout={async () => {
                // Hard skip: mark email step done with empty brain-dump (so wizard advances)
                // Prototype shortcut — full design would add a separate /onboarding/skip-email endpoint
                setSubmitting(true);
                try {
                  await fetchApi('/onboarding/brain-dump', {
                    method: 'POST',
                    body: JSON.stringify({ text: '(skipped)' }),
                  });
                  refetch();
                } finally {
                  setSubmitting(false);
                }
              }}
              onBack={() => setView('default')}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Default view ────────────────────────────────────────────────────────────

function DefaultView({
  firstName: _firstName,
  addressDisplay: _addressDisplay,
  dannyAddress,
  samples: _samples,
  mailtoHref,
  userEmail,
  isGmail,
  isOutlook,
  pulseHint,
  showSpamHint,
  checkingReply,
  noReplyYet,
  onCheckForReply,
  onDidntGetIt,
  onSkip,
}: {
  firstName: string;
  addressDisplay: string;
  dannyAddress: string | null;
  samples: Array<{ raw: string; domain: string }>;
  mailtoHref: string;
  userEmail?: string;
  isGmail: boolean;
  isOutlook: boolean;
  pulseHint: boolean;
  showSpamHint: boolean;
  checkingReply: boolean;
  noReplyYet: boolean;
  onCheckForReply: () => void;
  onDidntGetIt: () => void;
  onSkip: () => void;
}) {
  // Pick ONE primary CTA based on the user's mail provider; the others
  // live behind "Try a different app".
  const primary =
    isGmail
      ? { label: 'Open Gmail', href: 'https://mail.google.com/mail/u/0/#inbox' }
      : isOutlook
        ? { label: 'Open Outlook', href: 'https://outlook.live.com/mail/' }
        : { label: 'Open Mail', href: mailtoHref };

  return (
    <>
      <p className="text-xs font-semibold tracking-widest text-primary-600 dark:text-primary-400 uppercase mb-3">
        Step 1 of 2
      </p>
      <h1 className="text-3xl font-semibold mb-3">Check your inbox</h1>
      <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-8 leading-relaxed">
        We sent a welcome email to{' '}
        <span className="font-medium text-zinc-900 dark:text-zinc-100">{userEmail}</span>.
        Reply to it with anything on your plate and we'll turn it into your first task list.
      </p>

      <Button asChild size="lg" className="w-full">
        <a
          href={primary.href}
          {...(primary.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          onClick={(e) => {
            if (primary.href === '#' || (!dannyAddress && primary.label === 'Open Mail')) {
              e.preventDefault();
            }
          }}
        >
          {primary.label}
        </a>
      </Button>

      {/* Once the user has replied, let them pull the result immediately
          instead of waiting on the 3s poll. The wizard auto-advances when
          emailDone flips; if it hasn't, we show the "not yet" hint below. */}
      <div className="mt-3 flex items-center gap-3">
        <Button
          variant="secondary"
          size="sm"
          onClick={onCheckForReply}
          disabled={checkingReply}
        >
          {checkingReply ? 'Looking for your reply…' : "I replied — check now"}
        </Button>
        {noReplyYet && !checkingReply && (
          <span className="text-xs text-zinc-500 dark:text-zinc-500">
            Haven't seen it yet — replies can take a moment. Try again shortly.
          </span>
        )}
      </div>

      {showSpamHint && (
        <p className="text-xs text-zinc-500 dark:text-zinc-500 mt-4">
          Still nothing? Check your spam folder — first emails sometimes land there.
        </p>
      )}

      <div className="flex items-center justify-between mt-12 pt-5 border-t border-zinc-200 dark:border-zinc-900">
        <button
          onClick={onDidntGetIt}
          className={[
            'text-sm text-zinc-500 dark:text-zinc-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors',
            pulseHint ? 'animate-pulse text-zinc-700 dark:text-zinc-200' : '',
          ].join(' ')}
        >
          I didn't get the email
        </button>
        <button
          onClick={onSkip}
          className="text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200 transition-colors"
        >
          Skip for now →
        </button>
      </div>
    </>
  );
}

// ── "Didn't get the email" view ─────────────────────────────────────────────

function DidntGetItView({
  addressDisplay,
  mailtoHref,
  dannyAddress,
  isGmail,
  isOutlook,
  resending,
  resendCooldown,
  textareaValue,
  submitting,
  submitError,
  onTextareaChange,
  onSubmit,
  onResend,
  onBack,
  onSkip,
}: {
  addressDisplay: string;
  mailtoHref: string;
  dannyAddress: string | null;
  isGmail: boolean;
  isOutlook: boolean;
  resending: boolean;
  resendCooldown: number;
  textareaValue: string;
  submitting: boolean;
  submitError: string | null;
  onTextareaChange: (v: string) => void;
  onSubmit: () => void;
  onResend: () => void;
  onBack: () => void;
  onSkip: () => void;
}) {
  return (
    <>
      <p className="text-xs font-semibold tracking-widest text-primary-600 dark:text-primary-400 uppercase mb-3">
        Step 1 of 2
      </p>
      <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">
        Let's get you unstuck
      </h1>
      <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-6 leading-relaxed">
        I sent the welcome email from{' '}
        <code className="text-primary-700 dark:text-primary-300 bg-zinc-100 dark:bg-zinc-900 px-1.5 py-0.5 rounded text-xs">
          {addressDisplay}
        </code>
        .
      </p>

      <div className="bg-zinc-50 border border-zinc-200 dark:bg-zinc-900 dark:border-zinc-800 rounded-xl overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-zinc-200 dark:border-zinc-800/60">
          <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200 mb-3">
            A couple of things to try first
          </h3>
          <ul className="space-y-2.5 text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
            <li className="flex gap-2">
              <span className="text-zinc-400 dark:text-zinc-600 flex-shrink-0">•</span>
              <span>
                <strong className="text-zinc-800 dark:text-zinc-300">Check your spam folder.</strong>{' '}
                First emails from a new sender sometimes land there. If you find it,
                mark it "not spam" so future emails come through.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-zinc-400 dark:text-zinc-600 flex-shrink-0">•</span>
              <span>
                <strong className="text-zinc-800 dark:text-zinc-300">Resend it.</strong>
                <button
                  onClick={onResend}
                  disabled={resending || resendCooldown > 0}
                  className="ml-2 px-3 py-1 text-xs font-medium rounded-md bg-zinc-200 hover:bg-zinc-300 disabled:opacity-50 disabled:cursor-not-allowed text-zinc-800 dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:text-zinc-200 transition-colors"
                >
                  {resending
                    ? 'Sending…'
                    : resendCooldown > 0
                      ? `Wait ${resendCooldown}s`
                      : 'Resend welcome email'}
                </button>
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-zinc-400 dark:text-zinc-600 flex-shrink-0">•</span>
              <span>
                <strong className="text-zinc-800 dark:text-zinc-300">Open your inbox.</strong>
                <span className="ml-2 inline-flex gap-1.5">
                  <Button asChild size="xs" variant={isGmail ? 'default' : 'secondary'}>
                    <a href="https://mail.google.com/mail/u/0/#inbox" target="_blank" rel="noopener noreferrer">
                      Gmail
                    </a>
                  </Button>
                  <Button asChild size="xs" variant={isOutlook ? 'default' : 'secondary'}>
                    <a href="https://outlook.live.com/mail/" target="_blank" rel="noopener noreferrer">
                      Outlook
                    </a>
                  </Button>
                  <Button asChild size="xs" variant="secondary">
                    <a
                      href={mailtoHref}
                      onClick={(e) => {
                        if (!dannyAddress) e.preventDefault();
                      }}
                    >
                      Apple Mail
                    </a>
                  </Button>
                </span>
              </span>
            </li>
          </ul>
        </div>

        <div className="px-5 py-5 bg-zinc-100/60 dark:bg-zinc-950/40">
          <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200 mb-2">
            Or just type your tasks here
          </h3>
          <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
            Separate them with commas or new lines — I'll figure out the rest.
            The welcome email still works whenever you want to use it.
          </p>
          <textarea
            value={textareaValue}
            onChange={(e) => onTextareaChange(e.target.value)}
            rows={5}
            placeholder={`call dentist, finish budget for Lisa, follow up with Sarah`}
            className="w-full bg-white border border-zinc-300 text-zinc-900 placeholder:text-zinc-400 dark:bg-zinc-900 dark:border-zinc-800 dark:text-zinc-200 dark:placeholder:text-zinc-600 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-primary-500 transition-colors resize-none"
          />
          {submitError && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-2">{submitError}</p>
          )}
          <div className="flex items-center gap-3 mt-3">
            <Button
              onClick={onSubmit}
              disabled={submitting || !textareaValue.trim()}
            >
              {submitting ? 'Adding…' : 'Add tasks'}
            </Button>
            <button
              onClick={onBack}
              className="text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-300 transition-colors"
            >
              ← Back to email
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end mt-8 pt-4 border-t border-zinc-200 dark:border-zinc-900">
        <button
          onClick={onSkip}
          className="text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-300 transition-colors"
        >
          Skip this for now →
        </button>
      </div>
    </>
  );
}

// ── "Skipped" view ──────────────────────────────────────────────────────────

function SkippedView({
  textareaValue,
  submitting,
  submitError,
  onTextareaChange,
  onSubmit,
  onContinueWithout,
  onBack,
}: {
  textareaValue: string;
  submitting: boolean;
  submitError: string | null;
  onTextareaChange: (v: string) => void;
  onSubmit: () => void;
  onContinueWithout: () => void;
  onBack: () => void;
}) {
  return (
    <>
      <p className="text-xs font-semibold tracking-widest text-primary-600 dark:text-primary-400 uppercase mb-3">
        Step 1 of 2
      </p>
      <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">
        No problem — type a few things below
      </h1>
      <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-6 leading-relaxed">
        I'll add them to your list. The welcome email is still waiting in your
        inbox whenever you want to try it later.
      </p>

      <p className="text-xs text-zinc-500 dark:text-zinc-500 mb-2 leading-relaxed">
        Separate them with commas or new lines — I'll figure out the rest.
      </p>
      <textarea
        value={textareaValue}
        onChange={(e) => onTextareaChange(e.target.value)}
        rows={8}
        autoFocus
        placeholder={`call dentist, finish budget for Lisa, follow up with Sarah, renew car insurance`}
        className="w-full bg-white border border-zinc-300 text-zinc-900 placeholder:text-zinc-400 dark:bg-zinc-900 dark:border-zinc-800 dark:text-zinc-200 dark:placeholder:text-zinc-600 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-primary-500 transition-colors resize-none mb-3"
      />
      {submitError && (
        <p className="text-xs text-red-600 dark:text-red-400 mb-3">{submitError}</p>
      )}

      <div className="flex items-center gap-4 mb-10">
        <Button
          onClick={onSubmit}
          disabled={submitting || !textareaValue.trim()}
        >
          {submitting ? 'Adding…' : 'Add tasks'}
        </Button>
        <button
          onClick={onContinueWithout}
          disabled={submitting}
          className="text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-300 transition-colors disabled:opacity-50"
        >
          Continue without adding tasks →
        </button>
      </div>

      <div className="pt-4 border-t border-zinc-200 dark:border-zinc-900">
        <button
          onClick={onBack}
          className="text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-300 transition-colors"
        >
          ← Back to email
        </button>
      </div>
    </>
  );
}
